import { AppLink } from "@/components/ui/app-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LocalDate } from "@/components/ui/local-date";
import { StandaloneBadge } from "@/components/work-items/standalone-badge";
import { ApiError, api } from "@/lib/api";
import { workItemHref } from "@/lib/project-href";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { Paperclip } from "lucide-react";

/**
 * `/projects/:slug/releases/:releaseId` — one release: a progress
 * bar plus its work items grouped by status. Subtasks are
 * excluded (they inherit the parent's release).
 */

const releaseStatusVariant: Record<string, "default" | "warning" | "secondary"> = {
  open: "default",
  locked: "warning",
  closed: "secondary",
};

/** Severity rank so the Priority column sorts urgent → low, not
 *  alphabetically. */
const priorityRank: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

type ReleaseWorkItem = {
  id: string;
  gitlabIssueId: string;
  globalId: number;
  title: string;
  priority: string;
  standalone: boolean;
  dueDate: string | null;
  status: { name: string; isClosed: boolean } | null;
  assignee: { name: string | null } | null;
  attachmentCount: number;
};

type ReleaseDetailResponse = {
  release: {
    id: string;
    name: string;
    description: string | null;
    dueDate: string | null;
    status: "open" | "locked" | "closed";
    gitlabMilestoneId: number | null;
  };
  project: { id: string; slug: string; group: { path: string } | null };
  workItems: ReleaseWorkItem[];
};

export const Route = createFileRoute("/_app/projects/$slug/releases/$releaseId/")({
  loader: async ({ params }) => {
    try {
      return await api.get<ReleaseDetailResponse>(
        `/api/projects/${params.slug}/releases/${params.releaseId}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: ReleaseDetailPage,
});

function ReleaseDetailPage() {
  const { release, workItems } = Route.useLoaderData();
  const { slug, releaseId } = Route.useParams();

  const total = workItems.length;
  const closed = workItems.filter((wi) => wi.status?.isClosed).length;
  const percent = total > 0 ? Math.round((closed / total) * 100) : 0;
  const standaloneCount = workItems.filter((wi) => wi.standalone).length;
  const issueCount = total - standaloneCount;

  const grouped = workItems.reduce<Record<string, ReleaseWorkItem[]>>((acc, wi) => {
    const statusName = wi.status?.name ?? "No Status";
    const bucket = acc[statusName] ?? [];
    bucket.push(wi);
    acc[statusName] = bucket;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-xl font-semibold">{release.name}</h2>
            <Badge variant={releaseStatusVariant[release.status] ?? "default"}>
              {release.status}
            </Badge>
          </div>
          {release.description && (
            <p className="text-muted-foreground text-sm">{release.description}</p>
          )}
          {release.dueDate && (
            <p className="text-muted-foreground text-sm">
              Due: <LocalDate value={release.dueDate} />
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/projects/$slug/releases" params={{ slug }}>
              All Releases
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/projects/$slug/releases/$releaseId/edit" params={{ slug, releaseId }}>
              Edit
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground font-medium">
                Progress: {closed} / {total} closed
                {standaloneCount > 0 && issueCount > 0 && (
                  <span className="text-xs text-muted-foreground ml-2">
                    ({issueCount} issue{issueCount === 1 ? "" : "s"} · {standaloneCount} task
                    {standaloneCount === 1 ? "" : "s"})
                  </span>
                )}
              </span>
              <span className="font-semibold text-primary">{percent}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-3">
              <div
                className="bg-primary h-3 rounded-full transition-all"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {Object.entries(grouped).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No work items assigned to this release yet.
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).map(([statusName, items]) => (
          <ReleaseStatusGroup key={statusName} statusName={statusName} items={items} />
        ))
      )}
    </div>
  );
}

function ReleaseStatusGroup({
  statusName,
  items,
}: {
  statusName: string;
  items: ReleaseWorkItem[];
}) {
  const columns: Array<DataTableColumn<ReleaseWorkItem>> = [
    {
      key: "title",
      header: "Title",
      sortAccessor: (wi) => wi.title,
      cellClassName: "font-medium",
      cell: (wi) => (
        <div className="flex items-center gap-2">
          <AppLink href={workItemHref(wi.globalId)} className="hover:text-primary hover:underline">
            {wi.title}
          </AppLink>
          {wi.standalone && <StandaloneBadge size="xs" />}
          {wi.attachmentCount > 0 && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground"
              title={`${wi.attachmentCount} attachment${wi.attachmentCount === 1 ? "" : "s"}`}
            >
              <Paperclip className="h-3 w-3" aria-hidden="true" />
              {wi.attachmentCount}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      sortAccessor: (wi) => priorityRank[wi.priority] ?? 99,
      cell: (wi) => (
        <Badge
          variant={
            wi.priority === "urgent"
              ? "destructive"
              : wi.priority === "high"
                ? "warning"
                : "outline"
          }
        >
          {wi.priority}
        </Badge>
      ),
    },
    {
      key: "assignee",
      header: "Assignee",
      sortAccessor: (wi) => wi.assignee?.name,
      cellClassName: "text-muted-foreground text-sm",
      cell: (wi) => wi.assignee?.name ?? "—",
    },
    {
      key: "dueDate",
      header: "Due Date",
      sortAccessor: (wi) => wi.dueDate,
      cellClassName: "text-muted-foreground text-sm",
      cell: (wi) => (wi.dueDate ? <LocalDate value={wi.dueDate} /> : "—"),
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{statusName}</CardTitle>
          <Badge variant="outline">{items.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="rounded-md border">
          <DataTable columns={columns} rows={items} rowKey={(wi) => wi.id} bare />
        </div>
      </CardContent>
    </Card>
  );
}
